import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCompass } from '@fortawesome/free-solid-svg-icons';
import { api, ApiError } from '../../lib/api/client';
import { useTour } from '../tour/context';
import { AddressFields, addressComplete, emptyAddress, type Address } from './address';

interface Account {
  accountId: string;
  name: string;
  status: string;
  swftEnabled?: boolean;
  doceEnabled?: boolean;
  physicalAddress?: Address;
  billingAddress?: Address;
}
interface User {
  userId: string;
  email: string;
  role: string;
  active: boolean;
}
interface Summary {
  transactions: { total: number; byType: Record<string, number>; byState: Record<string, number> };
  licenses: { total: number; allocated: number; available: number; utilizationPct: number };
  subscriptions: number;
  conversionCredits: number;
}

export function AccountConsole() {
  const qc = useQueryClient();
  const tour = useTour();

  const claims = useQuery({
    queryKey: ['claims'],
    queryFn: async () => {
      const s = await fetchAuthSession();
      return (s.tokens?.idToken?.payload ?? {}) as Record<string, unknown>;
    },
  });
  const hasAccount = Boolean(claims.data?.['custom:accountId']);

  const account = useQuery({ queryKey: ['account'], queryFn: () => api.get<Account>('/account'), enabled: hasAccount });
  const summary = useQuery({ queryKey: ['summary'], queryFn: () => api.get<Summary>('/reports/summary'), enabled: hasAccount });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<User[]>('/users'), enabled: hasAccount });

  /*
   * `sent` is reported, not assumed. The invite ROW is committed either way, so a failed send leaves the
   * invitee sitting under Pending having been told nothing — and this screen used to show that as an
   * ordinary success. That is how an IAM gap on the users lambda went unnoticed: every invite failed to
   * send for weeks and the console said nothing at all.
   */
  const [inviteNote, setInviteNote] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: (vars: { email: string; role: string }) => api.post<{ sent: boolean }>('/users/invite', vars),
    onSuccess: (r) => {
      setInviteNote(r?.sent === false
        ? 'Invitation created, but the email could not be sent — use Resend to try again.'
        : 'Invitation sent.');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e) => setInviteNote(e instanceof ApiError ? e.message : 'Could not send the invitation.'),
  });
  const rename = useMutation({
    mutationFn: (name: string) => api.patch('/account', { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['account'] }),
  });

  if (claims.isLoading || (!hasAccount && !claims.isError)) {
    return <div className="loading-screen">// loading your account…</div>;
  }

  // First login: no account yet — ask for the organization name instead of defaulting to the email domain.
  if (claims.isSuccess && !hasAccount) {
    return (
      <SetupAccount
        onDone={async () => {
          await fetchAuthSession({ forceRefresh: true });
          qc.invalidateQueries({ queryKey: ['claims'] });
        }}
      />
    );
  }

  const s = summary.data;
  return (
    <div>
      <div className="console-head">
        <div>
          <AccountName name={account.data?.name} onSave={(n) => rename.mutate(n)} saving={rename.isPending} />
          <div className="sub">
            Welcome back — here's your account at a glance.{' '}
            <span className="acct-code">{account.data?.accountId}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link to="/app/capture" className="btn btn-primary">+ New EFT transaction</Link>
        </div>
      </div>

      {!tour.done && (
        <div className="tour-callout">
          <FontAwesomeIcon icon={faCompass} className="tour-callout-ico" fixedWidth />
          <div className="tour-callout-text">
            <strong>New to EFTCreator?</strong>
            A one-minute tour shows you where everything is — from installing the Device Service to
            tracking a submission.
          </div>
          <div className="tour-callout-actions">
            <button className="btn btn-ghost btn-sm" onClick={tour.dismiss}>Dismiss</button>
            <button className="btn btn-primary btn-sm" onClick={tour.start}>Take the tour</button>
          </div>
        </div>
      )}

      <div className="quick-actions" data-tour="quick-actions">
        <Link to="/app/capture/atf4" className="quick-card"><span className="wf-tag">ATF · eForm 1 &amp; 4</span><h4>Capture ATF</h4></Link>
        {account.data?.doceEnabled && (
          <Link to="/app/capture/doce" className="quick-card"><span className="wf-tag">FBI · DOCE</span><h4>Capture DOCE</h4></Link>
        )}
        <Link to="/app/capture/conv-atf4" className="quick-card"><span className="wf-tag">Card conversion</span><h4>Convert a card</h4></Link>
        <Link to="/app/store" className="quick-card"><span className="wf-tag">Store</span><h4>Buy a scanner</h4></Link>
      </div>

      <div className="stats">
        <Stat label="Transactions" value={s?.transactions.total ?? '—'} />
        <Stat label="Active subscriptions" value={s?.subscriptions ?? '—'} />
        <Stat
          label="License utilization"
          value={s ? `${s.licenses.allocated}/${s.licenses.total}` : '—'}
          unit={s ? `${s.licenses.utilizationPct}%` : undefined}
        />
        <Stat label="Conversion credits" value={s?.conversionCredits ?? '—'} />
      </div>

      {s && (Object.keys(s.transactions.byType).length > 0) && (
        <>
          <div className="section-title">Transactions by workflow</div>
          <div className="stats">
            {Object.entries(s.transactions.byType).map(([type, n]) => (
              <Stat key={type} label={type} value={n} />
            ))}
          </div>
        </>
      )}

      <div className="section-title">Team</div>
      <div className="panel">
        <ul className="team">
          {users.data?.map((u) => (
            <li key={u.userId}>
              <span className="who">
                <span className="avatar">{u.email.slice(0, 2).toUpperCase()}</span>
                <span>
                  {u.email}
                  {!u.active && <span style={{ color: 'var(--text-faint)' }}> · inactive</span>}
                </span>
              </span>
              <span className="role-pill">{u.role}</span>
            </li>
          ))}
        </ul>
        <InviteForm onInvite={(email, role) => { setInviteNote(null); invite.mutate({ email, role }); }} pending={invite.isPending} />
        {inviteNote && (
          <p className={invite.isError || inviteNote.includes('could not') ? 'cart-error' : 'field-help'}>
            {inviteNote}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * First-run: name the organization + capture physical/billing address before the account is created.
 *
 * `returning` marks a customer who signed in with credentials that already existed in v1. They see the same
 * form — we genuinely need these two things, because v1 stored neither an organization name nor a structured
 * address anywhere we can read one — but headed and worded as picking up an existing account rather than
 * starting a new one. Presented identically to a brand-new signup, it read to long-standing customers as
 * having been made to start over, and produced a run of tickets asking whether their history was gone.
 */
export function SetupAccount({ onDone, returning = false }: { onDone: () => void; returning?: boolean }) {
  const [name, setName] = useState('');
  const [physical, setPhysical] = useState<Address>(emptyAddress());
  const [billing, setBilling] = useState<Address>(emptyAddress());
  const [billingSame, setBillingSame] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canSubmit =
    name.trim() && addressComplete(physical) && (billingSame || addressComplete(billing)) && accepted;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post('/accounts', {
        name: name.trim(),
        physicalAddress: physical,
        billingSameAsPhysical: billingSame,
        acceptedTerms: accepted,
        ...(billingSame ? {} : { billingAddress: billing }),
      });
      onDone();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Could not create your account');
      setBusy(false);
    }
  }

  return (
    <div className="panel setup-account" style={{ maxWidth: 620, margin: '48px auto' }}>
      <h1 style={{ marginTop: 0 }}>{returning ? 'Welcome back — we found your account' : 'Set up your account'}</h1>
      {returning ? (
        <>
          <p className="sub" style={{ marginBottom: 12 }}>
            Your existing EFTCreator account was recognized from your sign-in, and we're moving it onto the
            new system now. Nothing needs to be set up again and nothing needs to be re-purchased.
          </p>
          {/*
            * Named individually because "your data will be migrated" is exactly the reassurance nobody
            * believes. These are the five things the migration actually moves.
            */}
          <ul className="setup-carryover">
            <li>Your past transactions and EFTs</li>
            <li>Your registered scanners and licenses</li>
            <li>Your capture locations</li>
            <li>Your subscription and any remaining credits</li>
            <li>Your billing history with Stripe</li>
          </ul>
          <p className="sub" style={{ marginBottom: 20 }}>
            We only need the two details below, because the old system never stored them in a form we can
            carry across. You can change either of them later under My Account.
          </p>
        </>
      ) : (
        <p className="sub" style={{ marginBottom: 20 }}>
          Welcome to EFTCreator. Tell us about your organization to get started — you can change any of
          this later under My Account.
        </p>
      )}
      <form onSubmit={submit}>
        <label className="field">
          <span className="field-label">Organization name</span>
          <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Fingerprinting LLC" required />
        </label>

        <div className="section-title" style={{ marginTop: 22 }}>Physical address</div>
        <AddressFields idPrefix="phys" value={physical} onChange={setPhysical} />

        <label className="checkbox-row" style={{ marginTop: 18 }}>
          <input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} />
          <span>Billing address is the same as the physical address</span>
        </label>

        {!billingSame && (
          <>
            <div className="section-title" style={{ marginTop: 18 }}>Billing address</div>
            <AddressFields idPrefix="bill" value={billing} onChange={setBilling} />
          </>
        )}

        <label className="checkbox-row" style={{ marginTop: 20 }}>
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          <span>
            I have read and agree to the{' '}
            <a href="/legal/terms" target="_blank" rel="noreferrer">Terms of Use</a> and{' '}
            <a href="/legal/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
          </span>
        </label>

        {err && <p className="cart-error" style={{ marginTop: 14 }}>{err}</p>}
        <button className="btn btn-primary" type="submit" disabled={busy || !canSubmit} style={{ marginTop: 20 }}>
          {busy
            ? (returning ? 'Setting up…' : 'Creating…')
            : (returning ? 'Continue to my account →' : 'Create account →')}
        </button>
        {returning && (
          <p className="field-help" style={{ marginTop: 10 }}>
            Your transactions and settings are copied across in the background — they may take a few minutes
            to appear after you continue.
          </p>
        )}
      </form>
    </div>
  );
}

/** My Account: view + edit physical/billing address. */
/** Account name shown as the dashboard title, editable inline. */
function AccountName({ name, onSave, saving }: { name: string | undefined; onSave: (n: string) => void; saving: boolean }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');

  if (editing) {
    return (
      <form
        className="inline-form"
        style={{ alignItems: 'center' }}
        onSubmit={(e) => { e.preventDefault(); if (val.trim()) { onSave(val.trim()); setEditing(false); } }}
      >
        <input className="input" autoFocus value={val} onChange={(e) => setVal(e.target.value)}
          style={{ minWidth: 220, fontSize: 20 }} />
        <button className="btn btn-primary btn-sm" type="submit" disabled={saving || !val.trim()}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn-ghost btn-sm" type="button" onClick={() => setEditing(false)}>Cancel</button>
      </form>
    );
  }

  return (
    <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {name ?? 'Dashboard'}
      <button
        className="btn btn-ghost btn-sm"
        title="Rename account"
        onClick={() => { setVal(name ?? ''); setEditing(true); }}
        style={{ fontSize: 13, fontWeight: 400 }}
      >
        Edit
      </button>
    </h1>
  );
}

function Stat({ label, value, unit }: { label: string; value: string | number; unit?: string | undefined }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">
        {value} {unit && <span className="unit">{unit}</span>}
      </div>
    </div>
  );
}

function InviteForm({ onInvite, pending }: { onInvite: (email: string, role: string) => void; pending: boolean }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Operator');
  return (
    <form
      className="invite"
      onSubmit={(e) => {
        e.preventDefault();
        if (email) onInvite(email, role);
        setEmail('');
      }}
    >
      <input
        className="input"
        type="email"
        placeholder="teammate@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
        <option>Operator</option>
        <option>Viewer</option>
        <option>AccountManager</option>
        <option>AccountAdmin</option>
      </select>
      <button type="submit" className="btn btn-primary btn-sm" disabled={pending}>
        {pending ? 'Inviting…' : 'Invite'}
      </button>
    </form>
  );
}
